import type {
  ExtensionFormTabSubmissionsRequest,
  GetListResponseOfExtensionFormTabSubmissionResponse,
  GetListResponseOfModuleExtensionFormTabResponse,
  GetModuleExtensionFormTabListRequest,
  InstanceSettingsRequest,
  InstanceSettingsResponse,
  ModuleExtensionActiveResponse,
  ModuleExtensionFormTabRequest,
  ModuleExtensionFormTabResponse,
} from 'src/api/ProactApiClient';
import {moduleExtensionApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery, voidClientQuery} from '../clientQuery';

export const moduleExtensionApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getActiveExtensions: builder.query<ModuleExtensionActiveResponse, void>({
      queryFn: clientQuery(() =>
        moduleExtensionApiClient.getActiveExtensions()
      ),
      providesTags: ['ModuleExtensions'],
    }),
    createFormTab: builder.mutation<
      ModuleExtensionFormTabResponse,
      ModuleExtensionFormTabRequest
    >({
      queryFn: clientQuery(params =>
        moduleExtensionApiClient.createFormTab(params)
      ),
      invalidatesTags: ['FormTabs', 'ModuleExtensions'],
    }),
    updateFormTab: builder.mutation<
      ModuleExtensionFormTabResponse,
      ModuleExtensionFormTabRequest
    >({
      queryFn: clientQuery(params =>
        moduleExtensionApiClient.updateFormTab(params)
      ),
      invalidatesTags: ['FormTabs', 'ModuleExtensions'],
    }),
    deleteFormTab: builder.mutation<void, number>({
      queryFn: voidClientQuery(id => moduleExtensionApiClient.deleteFormTab(id)),
      invalidatesTags: ['FormTabs', 'ModuleExtensions'],
    }),
    listFormTabs: builder.query<
      GetListResponseOfModuleExtensionFormTabResponse,
      GetModuleExtensionFormTabListRequest
    >({
      queryFn: clientQuery(params =>
        moduleExtensionApiClient.listFormTabs(params)
      ),
      providesTags: ['FormTabs'],
    }),
    getFormTabSubmissions: builder.query<
      GetListResponseOfExtensionFormTabSubmissionResponse,
      ExtensionFormTabSubmissionsRequest
    >({
      queryFn: clientQuery(params =>
        moduleExtensionApiClient.getFormTabSubmissions(params)
      ),
      providesTags: ['FormTabSubmissions'],
    }),
    getInstanceSettings: builder.query<InstanceSettingsResponse, void>({
      // API sends explicit null where the generated type says undefined;
      // normalize at this boundary so components see undefined.
      queryFn: clientQuery(async () => {
        const res = await moduleExtensionApiClient.getSettings();
        return {
          name: res.name,
          logoUrl: res.logoUrl ?? undefined,
          letterheadAddress: res.letterheadAddress ?? undefined,
          letterheadPhone: res.letterheadPhone ?? undefined,
          letterheadEmail: res.letterheadEmail ?? undefined,
          letterheadWebsite: res.letterheadWebsite ?? undefined,
        };
      }),
      providesTags: ['InstanceSettings'],
    }),
    updateInstanceSettings: builder.mutation<
      InstanceSettingsResponse,
      InstanceSettingsRequest
    >({
      queryFn: clientQuery(params =>
        moduleExtensionApiClient.updateSettings(params)
      ),
      invalidatesTags: ['InstanceSettings'],
    }),
  }),
});

export const {
  useGetActiveExtensionsQuery,
  useCreateFormTabMutation,
  useUpdateFormTabMutation,
  useDeleteFormTabMutation,
  useListFormTabsQuery,
  useGetFormTabSubmissionsQuery,
  useGetInstanceSettingsQuery,
  useUpdateInstanceSettingsMutation,
} = moduleExtensionApi;
